'use client';

import classnames from 'classnames';
// usePathname from @navigation is returning different results
// eslint-disable-next-line no-restricted-imports
import { usePathname } from 'next/navigation';
import { ChevronDown, ChevronUp, User } from '@components/icons';
import GlobalNav from '@components/v3/organisms/global-nav';
import { SiteHeader } from '@components/v3/organisms/site-header';
import SignInNav from '@components/v3/organisms/sign-in-nav';
import { signIn, signOut, useSession } from 'next-auth/react';
import { useState } from 'react';
import { localeToContentfulLocale } from '@utils/locales.utils';
import { SearchPanelModal } from '@stadionhq-v3/algolia-ui';
import {
  newsTransformer,
  pagesTransformer,
  videosTransformer,
} from '@utils/transforms/components/organisms/searchPanelModal.transform';
import { generateInternalLinkUrl } from '@utils/paths/generateInternalLinkUrl.utils';
import { SiteNavProps } from './siteNav.types';
import { useHeaderSponsors } from './headerSponsorContext';
import HeaderSponsorLink from './HeaderSponsorLink';
import styles from './siteNav.module.scss';

const SiteNav = ({
  /* props */
  nativeProps,
  classNames,
  globalNav,
  siteHeader,
  testId,
  languageOptions,
  locale,
  searchCtas,
  dictionary,
}: SiteNavProps) => {
  const { sponsors: headerSponsors } = useHeaderSponsors();
  const isAuthEnabled =
    !!process.env.NEXT_PUBLIC_ACCOUNT_PAGE_URL &&
    !!process.env.NEXT_PUBLIC_ACCOUNT_LOGOUT_URL;
  const classes = classnames([styles.siteNav], classNames);
  const [isSearchOverlayOpen, setIsSearchOverlayOpen] =
    useState<boolean>(false);
  const contentfulLocale = localeToContentfulLocale(locale);

  const auth = useSession();

  // TODO: Detect FusionAuth domain logout.
  // FusionAuth SSO session logout doesn't revoke OAuth tokens, so the local
  // session persists until the access token expires or the refresh fails.

  const pathname = usePathname();
  const name = auth.data?.user?.name ?? '';

  const handleLogIn = async () => {
    signIn('default');
  };

  const handleRegister = async () => {
    signIn('register');
  };

  const handleSignOut = async () => {
    await signOut({ redirect: false });
    // FA's /account/logout strips `post_logout_redirect_uri` when chaining
    // internally to /oauth2/logout (confirmed in earlier network traces), so
    // there's no point passing it — FA always falls back to the application's
    // configured Logout URL for the final landing. That URL now points at
    // /[locale]/auth/sso-logout, which handles the user-facing redirect.
    window.location.href = process.env.NEXT_PUBLIC_ACCOUNT_LOGOUT_URL!;
  };

  if (
    !process.env.NEXT_PUBLIC_ALGOLIA_APP_ID ||
    !process.env.NEXT_PUBLIC_ALGOLIA_PUBLIC_API_KEY ||
    !process.env.NEXT_PUBLIC_ALGOLIA_INDEX_NAME
  ) {
    throw new Error(
      'NEXT_PUBLIC_ALGOLIA_APP_ID or NEXT_PUBLIC_ALGOLIA_PUBLIC_API_KEY or NEXT_PUBLIC_ALGOLIA_INDEX_NAME env variable is not defined'
    );
  }

  const signInNavProps = isAuthEnabled
    ? {
        isSignedIn: auth.status === 'authenticated',
        ssoLink1: { text: 'Login', onClick: handleLogIn },
        ssoLink2: { text: 'Register', onClick: handleRegister },
        userIcon: <User />,
        myAccount: {
          text: 'My Account',
          closedIcon: <ChevronDown />,
          openIcon: <ChevronUp />,
          isOpen: false,
        },
        dropdownData: {
          signOutButton: {
            text: 'Sign Out',
            onClick: handleSignOut,
          },
          userData: {
            userName: name,
            userMembershipLevel: '',
            userMembershipNumber: '',
            userMembershipType: '',
            detailsLink: {
              title: 'Manage Account',
              url: process.env.NEXT_PUBLIC_ACCOUNT_PAGE_URL!,
            },
          },
        },
      }
    : undefined;

  return (
    <header
      {...nativeProps}
      className={classes}
      data-component="SiteNav"
      data-testid={testId}
    >
      {globalNav && (
        <GlobalNav
          {...globalNav}
          currentUrl={pathname}
          ssoNav={
            signInNavProps ? (
              <SignInNav onDark {...signInNavProps} />
            ) : undefined
          }
        />
      )}

      <SiteHeader
        {...siteHeader}
        currentUrl={pathname}
        languageOptions={languageOptions}
        ssoNav={
          signInNavProps && !globalNav ? (
            <SignInNav {...signInNavProps} />
          ) : undefined
        }
        additionalTriggers={headerSponsors.map((sponsor) => (
          <HeaderSponsorLink key={sponsor.url ?? sponsor.title} {...sponsor} />
        ))}
        searchOverlayTrigger={undefined}
        searchOverlay={
          <SearchPanelModal
            show
            onClose={() => setIsSearchOverlayOpen(false)}
            searchPanel={{
              appId: process.env.NEXT_PUBLIC_ALGOLIA_APP_ID,
              publicApiKey: process.env.NEXT_PUBLIC_ALGOLIA_PUBLIC_API_KEY,
              indexName: process.env.NEXT_PUBLIC_ALGOLIA_INDEX_NAME,
              title: dictionary?.search,
              locale: contentfulLocale,
              dictionary: {
                voiceDictation: dictionary?.voiceDictation,
                inputLabel: dictionary?.inputLabel,
                cancel: dictionary?.cancel,
                search: dictionary?.search,
                number: dictionary?.number,
                close: dictionary?.close,
                noResultsTitle: dictionary?.noResultsTitle,
                noResultsCopy: dictionary?.noResultsCopy,
                helpText: dictionary?.helpText,
              },
              filters: [
                {
                  attribute: 'all',
                  facets: [
                    {
                      attribute: 'pages',
                      criteria: [
                        'sys.contentType.sys.id:contentPage',
                        `fields.hideForThisLocale.${contentfulLocale}:false`,
                      ],
                      nbHits: 6,
                    },
                    {
                      attribute: 'news',
                      criteria: [
                        'sys.contentType.sys.id:articlePage',
                        `fields.hideForThisLocale.${contentfulLocale}:false`,
                      ],
                      nbHits: 6,
                    },
                    {
                      attribute: 'videos',
                      criteria: 'sys.contentType.sys.id:video',
                      nbHits: 6,
                    },
                  ],
                  tabLabel: dictionary?.searchAllTabLabel,
                },
                {
                  attribute: 'pages',
                  facets: [
                    {
                      attribute: 'pages',
                      criteria: [
                        'sys.contentType.sys.id:contentPage',
                        `fields.hideForThisLocale.${contentfulLocale}:false`,
                      ],
                      nbHits: 6,
                    },
                  ],
                  tabLabel: dictionary?.searchPagesTabLabel,
                  results: {
                    sectionLabel: dictionary.searchPagesSectionLabel,
                    transformFunction: pagesTransformer,
                    type: 'link',
                    ...(searchCtas?.pageCta &&
                      searchCtas?.pageCta?.url &&
                      searchCtas?.pageCta?.title &&
                      generateInternalLinkUrl(searchCtas?.pageCta, locale)
                        ?.url && {
                        cta: {
                          url:
                            generateInternalLinkUrl(searchCtas?.pageCta, locale)
                              ?.url || '',
                          title: searchCtas?.pageCta?.title,
                        },
                      }),
                  },
                },
                {
                  attribute: 'news',
                  facets: [
                    {
                      attribute: 'news',
                      criteria: [
                        'sys.contentType.sys.id:articlePage',
                        `fields.hideForThisLocale.${contentfulLocale}:false`,
                      ],
                      nbHits: 6,
                    },
                  ],
                  tabLabel: dictionary?.searchNewsTabLabel,
                  results: {
                    sectionLabel: dictionary?.searchNewsSectionLabel,
                    transformFunction: newsTransformer,
                    type: 'card',
                    ...(searchCtas?.newsCta &&
                      searchCtas?.newsCta?.url &&
                      searchCtas?.newsCta?.title &&
                      generateInternalLinkUrl(searchCtas?.newsCta, locale)
                        ?.url && {
                        cta: {
                          url:
                            generateInternalLinkUrl(searchCtas?.newsCta, locale)
                              ?.url || '',
                          title: searchCtas?.newsCta?.title,
                        },
                      }),
                  },
                },
                {
                  attribute: 'videos',
                  facets: [
                    {
                      attribute: 'videos',
                      criteria: 'sys.contentType.sys.id:video',
                      nbHits: 6,
                    },
                  ],
                  tabLabel: dictionary?.searchVideoTabLabel,
                  results: {
                    sectionLabel: dictionary?.searchVideoSectionLabel,
                    transformFunction: videosTransformer,
                    type: 'card',
                    ...(searchCtas?.videoCta &&
                      searchCtas?.videoCta?.url &&
                      searchCtas?.videoCta?.title &&
                      generateInternalLinkUrl(searchCtas?.videoCta, locale)
                        ?.url && {
                        cta: {
                          url:
                            generateInternalLinkUrl(
                              searchCtas?.videoCta,
                              locale
                            )?.url || '',
                          title: searchCtas?.videoCta?.title,
                        },
                      }),
                  },
                },
              ],
            }}
          />
        }
        isSearchOverlayOpen={isSearchOverlayOpen}
      />
    </header>
  );
};

export default SiteNav;
